import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {BymoPagePropsContentSettingsMetadata, DoppeHideableValue} from '../../client-server-common';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    doppeDtoBlogPostGetMedia,
    DoppeExternalBlogPostProvider,
    doppeExternalBlogPostProviderResolveBusinessEntityReference,
    doppeHideableValueCreateHidden,
    doppeHideableValueGetVisibleValue
} from '../../client-server-common';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    arrayRemoveNullValues,
    asArray,
    dateConvertToIsoString,
    dateExtractLocalDateAsUtcMidnightIsoString,
    dateGetCurrentDateTime,
    immerProduce,
    RichTextContent
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import type {DoppeDtoBlogPost} from '../../client-server-common/types/doppe-dto-blog-post';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsResetSettingsForLock
} from '../../client-server-common/types/doppe-action-cta-button';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    doppeBlogPostActionGetMediaViewDefaultSettings
} from '../../doppe-action-types-client-server-common/doppe-blog-post-action-types-utils/doppe-blog-post-action-types-utils';

export namespace DoppeActionTypeHoppBlogPost {

    export interface BaseActionSettings extends DoppeActionMediaViewSettings {
        blogPostTitle: string;
        description: RichTextContent;
        blogPostImages: WixMediaResource[] | null;
        blogPostDate: string;
        blogPostLink: string;
        blogPostProvider: DoppeExternalBlogPostProvider | null;
        blogPostProviderId: string | null;
        ribbon: DoppeHideableValue<string>;
    }

    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeHoppBlogPostActionContentSettingsMetadata = {
    blogPostTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    blogPostImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    blogPostDate: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate),
    blogPostLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeHoppBlogPost.ActionSettings>



export const doppeActionTypeHoppBlogPost = doppeActionTypeDefineMetadata<DoppeActionTypeHoppBlogPost.ActionSettings>({
    id: 'io.bymo.action/hopp-blog-post',
    resolveActionSettings: action => {

        const {
            ctaButtons,
            sections,
            mainMediaViewSettings,
            mainMediaViewLayout,
            ribbon,
            ...restSettings
        } = action.settings;

        return {
            ribbon: doppeHideableValueCreateHidden(''),
            blogPostTitle: '',
            description: '',
            blogPostImages: null,
            blogPostDate: dateConvertToIsoString(dateGetCurrentDateTime()),
            blogPostLink: '',
            blogPostProvider: DoppeExternalBlogPostProvider.Hopp,
            blogPostProviderId: null,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeHoppBlogPostActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                description: true,
                ribbon: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.blogPostLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.blogPostImages, []) ?? []));
    },
    resolveMainMedia: actionSettings => {
        const mediaItems = arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.blogPostImages, []) ?? []));
        return mediaItems[0] ?? null;
    },
    resolveMainLink: (actionSettings, htmlSiteInfo, viewerLang, bymoPageProps, viewerPagesRoutesManager) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            htmlSiteInfo: htmlSiteInfo,
            sourceURL: actionSettings.blogPostLink,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager
        }) ?? null;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            if (actionSettings.blogPostImages) {
                actionSettings.blogPostImages = actionSettings.blogPostImages.map(
                    image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap))
            }
        })
    },
    resolveUsedBusinessEntities: actionSettings => {
        return arrayRemoveNullValues([
            actionSettings.blogPostProvider !== null && actionSettings.blogPostProviderId !== null ?
                doppeExternalBlogPostProviderResolveBusinessEntityReference(
                    actionSettings.blogPostProvider,
                    actionSettings.blogPostProviderId
                ) : null
        ])
    }
});

export function doppeActionTypeHoppBlogPostResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeHoppBlogPost.BaseActionSettings,
    blogPost: DoppeDtoBlogPost
): DoppeActionTypeHoppBlogPost.BaseActionSettings {

    return {
        ...actionSettings,
        blogPostImages: doppeDtoBlogPostGetMedia(blogPost) ?? actionSettings.blogPostImages,
        blogPostTitle: blogPost.title ?? actionSettings.blogPostTitle,
        description: blogPost.excerpt ?? actionSettings.description,
        blogPostDate: dateExtractLocalDateAsUtcMidnightIsoString(blogPost.firstPublishedDate ?? actionSettings.blogPostDate),
        blogPostLink: blogPost.blogPostPageUrl ?? actionSettings.blogPostLink,
        blogPostProvider: DoppeExternalBlogPostProvider.Hopp,
        blogPostProviderId: blogPost.id,
        ...doppeBlogPostActionGetMediaViewDefaultSettings()
    }
}

export function convertDoppeDtoBlogPostToHoppBlogPostSettings(options: {
    blogPost: DoppeDtoBlogPost, 
    blogPostSettings?: DoppeActionTypeHoppBlogPost.ActionSettings
}) {

    const {
        blogPost,
        blogPostSettings = {},
    } = options;

    return {
        ribbon: doppeHideableValueCreateHidden(''),
        ...doppeActionContentSectionSettingsDefaultValuesMigration(blogPostSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({
            ctaButtons: [],
        }, false),
        ...blogPostSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(blogPostSettings),
        blogPostTitle: blogPost.title ?? '',
        description: blogPost.excerpt ?? '',
        blogPostImages: doppeDtoBlogPostGetMedia(blogPost),
        blogPostDate: dateExtractLocalDateAsUtcMidnightIsoString(blogPost.firstPublishedDate ?? dateGetCurrentDateTime()),
        blogPostLink: blogPost.blogPostPageUrl ?? '',
        blogPostProvider: DoppeExternalBlogPostProvider.Hopp,
        blogPostProviderId: blogPost.id,
    } satisfies DoppeActionTypeHoppBlogPost.ActionSettings
} 